import type { SalesPortalCheckoutProgress } from 'types';

import type { CheckoutFormValues, WhoIsThisFormOption } from './CheckoutContext';
import { CheckoutStep, CHECKOUT_STEP_ORDER } from './checkoutFlowStates';

// Same-session persistence of the checkout's non-payment fields (spec Item 2):
// a buyer who abandons and returns in the same browser session gets their entered
// details back. Persisted via an explicit allowlist — payment fields must never
// reach storage, and agreement/waiver attestations reset by design (spec Item 5:
// "initial/checkbox state resets on browser refresh — not persisted").
//
// The recovery magic link (spec Item 11) reuses the same mechanism: the landing page
// writes the server-held snapshot here, tagged `source: 'email'`, then routes into
// the checkout, which restores form + step exactly as a same-session return would.

const STORAGE_KEY_PREFIX = 'kilo.salesPortalCheckout';

export type CheckoutResumeSource = 'session' | 'email';

// dob is stored as an ISO string; everything else round-trips as-is.
type PersistedAthleteInformation = Omit<CheckoutFormValues['athleteInformation'], 'dob'> & {
  dob: string | null;
};

export interface PersistedCheckoutFields {
  whoIsThisFor: WhoIsThisFormOption;
  profileId: string | undefined;
  athleteInformation: PersistedAthleteInformation;
  // The accordion step the buyer was on when the snapshot was taken, so a resume
  // reopens there instead of at the first step. Absent on pre-Item-11 snapshots.
  currentStep?: CheckoutStep | null;
  source?: CheckoutResumeSource;
}

export function buildCheckoutStorageKey(gymId: string, packageId: string, billingPlanId: string): string {
  return `${STORAGE_KEY_PREFIX}.${gymId}.${packageId}.${billingPlanId}`;
}

function writeSnapshot(storageKey: string, snapshot: PersistedCheckoutFields): void {
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
  } catch {
    // Storage can be unavailable (private browsing, quota) — resume is best-effort.
  }
}

export function persistCheckoutFields(
  storageKey: string,
  values: CheckoutFormValues,
  currentStep: CheckoutStep | null = null,
): void {
  const snapshot: PersistedCheckoutFields = {
    whoIsThisFor: values.whoIsThisFor,
    profileId: values.profileId,
    athleteInformation: {
      ...values.athleteInformation,
      dob: values.athleteInformation.dob ? values.athleteInformation.dob.toISOString() : null,
    },
    currentStep,
    source: 'session',
  };

  writeSnapshot(storageKey, snapshot);
}

export function readPersistedCheckoutFields(storageKey: string): PersistedCheckoutFields | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as PersistedCheckoutFields;
    if (!parsed || typeof parsed !== 'object' || !parsed.athleteInformation) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPersistedCheckoutFields(storageKey: string): void {
  try {
    window.sessionStorage.removeItem(storageKey);
  } catch {
    // Nothing to do — storage was never written.
  }
}

/** Removes the saved checkout progress of every package in this tab, e.g. when the buyer logs out. */
export function clearAllPersistedCheckoutFields(): void {
  try {
    const checkoutKeys = Array.from({ length: window.sessionStorage.length }, (_, index) =>
      window.sessionStorage.key(index),
    ).filter((key): key is string => !!key && key.startsWith(`${STORAGE_KEY_PREFIX}.`));
    checkoutKeys.forEach(key => window.sessionStorage.removeItem(key));
  } catch {
    // Nothing to do — storage is unavailable.
  }
}

export function toAthleteInformationFormValues(
  persisted: PersistedCheckoutFields,
): CheckoutFormValues['athleteInformation'] {
  return {
    ...persisted.athleteInformation,
    dob: persisted.athleteInformation.dob ? new Date(persisted.athleteInformation.dob) : null,
  };
}

// The steps a resume may mark complete ahead of the restored one. Account is
// session-derived, and the agreement + payment steps are never restored: the
// agreement is re-accepted per purchase (spec Item 5) and payment data never
// persists — so a buyer who left at Payment lands back on Agreement & Waiver.
const RESTORABLE_STEPS: CheckoutStep[] = ['billingPlan', 'whoIsThisFor', 'athleteInformation'];
const LAST_RESTORABLE_OPEN_STEP: CheckoutStep = 'agreementWaiver';

export interface RestoredStepState {
  openStep: CheckoutStep;
  completedSteps: CheckoutStep[];
}

export function deriveRestoredStepState(
  currentStep: CheckoutStep | null | undefined,
): RestoredStepState | null {
  if (!currentStep || !CHECKOUT_STEP_ORDER.includes(currentStep)) return null;

  const currentIndex = CHECKOUT_STEP_ORDER.indexOf(currentStep);
  const capIndex = CHECKOUT_STEP_ORDER.indexOf(LAST_RESTORABLE_OPEN_STEP);
  const openStep = CHECKOUT_STEP_ORDER[Math.min(currentIndex, capIndex)];
  const completedSteps = CHECKOUT_STEP_ORDER.slice(0, CHECKOUT_STEP_ORDER.indexOf(openStep)).filter(step =>
    RESTORABLE_STEPS.includes(step),
  );

  return {
    openStep,
    completedSteps: ['billingPlan', ...completedSteps.filter(step => step !== 'billingPlan')],
  };
}

// A server-held progress row may predate a column, so every buyer field is optional
// on the way in. There is no medical-notes column by design (spec Item 11 review):
// the buyer re-enters notes on resume.
export type RecoveredCheckoutProgress = Partial<Omit<SalesPortalCheckoutProgress, 'currentStep'>>;

// Writes a server-held recovery row into session storage so the checkout rehydrates
// it through the same path as a same-session return.
export function persistRecoveredCheckout(
  storageKey: string,
  progress: RecoveredCheckoutProgress,
  currentStep: CheckoutStep,
): void {
  const hasAddress =
    !!progress.addressLines ||
    !!progress.addressLocality ||
    !!progress.addressRegion ||
    !!progress.addressZipCode ||
    !!progress.addressCountry;
  const persisted: PersistedCheckoutFields = {
    whoIsThisFor: (progress.whoIsThisFor as WhoIsThisFormOption | null | undefined) ?? 'myself',
    profileId: progress.targetProfileId ?? undefined,
    athleteInformation: {
      athleteId: '',
      email: progress.email ?? '',
      firstName: progress.firstName ?? '',
      lastName: progress.lastName ?? '',
      phone: progress.phone ?? '',
      dob: progress.dob ?? null,
      gender: (progress.gender as CheckoutFormValues['athleteInformation']['gender']) ?? null,
      address: hasAddress
        ? {
            addressLines: progress.addressLines ?? null,
            locality: progress.addressLocality ?? null,
            region: progress.addressRegion ?? null,
            zipCode: progress.addressZipCode ?? null,
            country: (progress.addressCountry ?? 'US') as 'CA' | 'GB' | 'US',
          }
        : null,
      medicalNotes: null,
      emergencyName: progress.emergencyName ?? null,
      emergencyPhone: progress.emergencyPhone ?? null,
      surname: '',
    },
    currentStep,
    source: 'email',
  };

  writeSnapshot(storageKey, persisted);
}
